import { IApiClient } from "../api-client";
import { EnvType } from "../services";
import { AddClipsRequest } from "../Types/add-clips-request";
import { CreateAssetShareRequest } from "../Types/create-asset-share-request";
import { CreateClipsResponse } from "../Types/create-clips-response";
import { CreateShareMultipleRequest } from "../Types/create-share-multiple-request";
import { CreateShareRequest } from "../Types/create-share-request";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetAssetShareResponse } from "../Types/get-asset-share-response";
import { GetShareByKeyResponse } from "../Types/get-share-by-key-response";
import { GetShareInfosResponse } from "../Types/get-share-infos-response";
import { GetShareMetadataResponse } from "../Types/get-share-metadata-response";
import { GetShareResponse } from "../Types/get-share-response";
import { GetSharesResponse } from "../Types/get-shares-response";
import { ImportClipsRequest } from "../Types/import-clips-request";
import { ListShareAssetByIdResponse } from "../Types/list-share-asset-by-id-response";
import { UpdateShareReceipientsRequest } from "../Types/update-share-receipients-request";
import { UpdateShareRequest } from "../Types/update-share-request";

export interface IpLocationServiceApiResponse {
  lat: number;
  lon: number;
  country: string;
  regionName: string;
  city: string;
  isp: string;
}

interface IShareApiClient {
  GetAllShares(pageNb: number, pageSize: number): Promise<GetSharesResponse>;
  GetShareByKey(key: string): Promise<GetShareByKeyResponse>;
  GetShareInfos(): Promise<GetShareInfosResponse>;
  CreateShare(request: CreateShareRequest): Promise<GenericResponseStd>;
  CreateMultipleShare(request: CreateShareMultipleRequest): Promise<GenericResponseStd>;
  DeleteShareById(shareId: number): Promise<GenericResponseStd>;
  UpdateShare(request: UpdateShareRequest): Promise<GetShareResponse>;
  UpdateShareRecipients(request: UpdateShareReceipientsRequest): Promise<GetShareResponse>;
  AddClips(request: AddClipsRequest): Promise<CreateClipsResponse>;
  NotifyClips(request: AddClipsRequest): Promise<GenericResponseStd>;
  DeleteClip(key: string, clipId: number): Promise<GenericResponseStd>;
  ImportClips(request: ImportClipsRequest): Promise<GenericResponseStd>;
  CreateObjectShare(request: CreateAssetShareRequest): Promise<GenericResponseStd>;
  ListShareObjectByObjectId(objectId: number): Promise<ListShareAssetByIdResponse>;
  GetObjectShare(key: string): Promise<GetAssetShareResponse>;
  GetAllSharesByObjectId(objectId: number): Promise<GetSharesResponse>;
  GetShareMetadataById(shareId: number): Promise<GetShareMetadataResponse>;
  _GetIpLocation(ip: string): Promise<IpLocationServiceApiResponse>;
}

export default class ShareApiClient implements IShareApiClient {
  readonly servicePort = 9507;
  readonly serviceBaseUrl = "share-service/api";

  shareApiClient: IApiClient;

  constructor(shareApiClient: IApiClient, envType: EnvType) {
    this.shareApiClient = shareApiClient;
    if (envType === "local") this.shareApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetAllShares(pageNb: number, pageSize: number): Promise<GetSharesResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/${pageNb}/${pageSize}`);
  }
  async GetShareByKey(key: string): Promise<GetShareByKeyResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/${key}`);
  }
  async GetShareInfos(): Promise<GetShareInfosResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/views-infos`);
  }
  async CreateShare(request: CreateShareRequest): Promise<GenericResponseStd> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares`, request);
  }
  async CreateMultipleShare(request: CreateShareMultipleRequest): Promise<GenericResponseStd> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares/multiple`, request);
  }
  async DeleteShareById(shareId: number): Promise<GenericResponseStd> {
    return await this.shareApiClient.delete(`${this.serviceBaseUrl}/shares/${shareId}`);
  }
  async UpdateShare(request: UpdateShareRequest): Promise<GetShareResponse> {
    return await this.shareApiClient.put(`${this.serviceBaseUrl}/shares`, request);
  }
  async UpdateShareRecipients(request: UpdateShareReceipientsRequest): Promise<GetShareResponse> {
    return await this.shareApiClient.put(`${this.serviceBaseUrl}shares/recipients`, request);
  }
  async AddClips(request: AddClipsRequest): Promise<CreateClipsResponse> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares/clips`, request);
  }
  async NotifyClips(request: AddClipsRequest): Promise<GenericResponseStd> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares/clips/notify`, request);
  }
  async DeleteClip(key: string, clipId: number): Promise<GenericResponseStd> {
    return await this.shareApiClient.delete(`${this.serviceBaseUrl}/shares/${key}/clips/${clipId}`);
  }
  async ImportClips(request: ImportClipsRequest): Promise<GenericResponseStd> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares/clips/import`, request);
  }
  async CreateObjectShare(request: CreateAssetShareRequest): Promise<GenericResponseStd> {
    return await this.shareApiClient.post(`${this.serviceBaseUrl}/shares/object`, request);
  }
  async ListShareObjectByObjectId(objectId: number): Promise<ListShareAssetByIdResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/object/${objectId}`);
  }
  async GetObjectShare(key: string): Promise<GetAssetShareResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/object/key/${key}`);
  }
  async GetAllSharesByObjectId(objectId: number): Promise<GetSharesResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/byobject/${objectId}`);
  }
  async GetShareMetadataById(shareId: number): Promise<GetShareMetadataResponse> {
    return await this.shareApiClient.get(`${this.serviceBaseUrl}/shares/metadata/${shareId}`);
  }
  async _GetIpLocation(ip: string): Promise<IpLocationServiceApiResponse> {
    return await this.shareApiClient.get(`http://ip-api.com/json/${ip}`);
  }

  //------Utils------
  GetApiUrl(): string {
    return this.shareApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
